import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks";
import { PostType } from "../../types/types";
import { usePostRequest } from "../../hooks/postRequest";
import AppPagination from "../../components/AppPagination";
import Loading from "../../components/Loading";
import PostCard from "../../components/post/PostCard";
import Filter from "../../components/filters/DateFilter";
import SearchInput from "../../components/filters/SearchInput";
import RefreshButton from "../../components/filters/RefreshButton";
import { ADMIN_BASE_URL } from "../../utils/config";

const PostsList: React.FC = () => {
  const { search } = useLocation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { loading, data: postsData } = useAppSelector((state) => state.post);
  const [posts, setPosts] = useState<PostType[]>([]);
  const [lastPage, setLastPage] = useState<number>(1);
  const [searchText, setSearchText] = useState<string>("");
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const postRequest = usePostRequest();

  const fetchData = useCallback(async () => {
    try {
      await postRequest({
        baseUrl: ADMIN_BASE_URL,
        method: "GET",
        data: null,
        id: null,
        perPage: 12,
      });
    } catch (error) {
      console.error("Failed to fetch posts:", error);
    }
  }, [postRequest]);

  useEffect(() => {
    if (postsData) {
      const { meta, data } = postsData;
      setLastPage(meta?.lastPage || 1);
      setPosts(data);
    }
  }, [postsData]);

  useEffect(() => {
    fetchData();
  }, [search, fetchData]);

  const eachPost = useMemo(
    () =>
      posts.length > 0 &&
      posts.map((post: PostType, index: number) => (
        <Grid item xs={12} sm={12} md={6} lg={4} key={index}>
          <PostCard index={index} post={post} fetchData={fetchData} />
        </Grid>
      )),
    [posts, fetchData]
  );

  return (
    <Box sx={{ p: 3, borderRadius: "12px" }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Posts List
      </Typography>
      {loading && <Loading />}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3} xl={2}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/create-new-post")}
          >
            Add New Post
          </Button>
        </Grid>
        <Grid item xs={12} md={9} xl={6}>
          <Filter />
        </Grid>
        <Grid
          item
          xs={12}
          md={12}
          xl={4}
          sx={{
            display: "flex",
            justifyContent: isSmallScreen ? "center" : "flex-end",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <SearchInput
              searchText={searchText}
              setSearchText={setSearchText}
              placeholderText="Search by title"
            />
            <Box sx={{ ml: 2 }}>
              <RefreshButton />
            </Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {posts.length > 0 ? (
          eachPost
        ) : (
          <Grid item xs={12}>
            <Typography align="center">No posts found</Typography>
          </Grid>
        )}
      </Grid>
      <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
        <AppPagination pageCount={lastPage} />
      </Box>
    </Box>
  );
};

export default PostsList;
